import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, Save, Monitor, Wifi, WifiOff, Cpu, HardDrive, 
  Calendar, DollarSign, Building, Shield, AlertTriangle, Ticket,
  Clock, User, RotateCcw, AlertCircle, UserCheck, UserMinus, History,
  FileText, Search, Link2, Unlink, Receipt, Printer, Copy, Upload, Trash2, Download, Package, Archive
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';

const DEPARTMENTS = [
  'IT', 'Sales', 'Marketing', 'Finance', 'HR', 'Operations', 
  'Production', 'Warehouse', 'Executive', 'Customer Service', 'Other'
];

// Gamer Packaging company ID in Business Central
const BC_COMPANY_ID = '7d84c6d5-81e2-eb11-86df-00224822baa7';

const AssetDetailPage = () => {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const [endpoint, setEndpoint] = useState(null);
  const [meta, setMeta] = useState({});
  const [linkedTickets, setLinkedTickets] = useState([]);
  const [checkoutHistory, setCheckoutHistory] = useState([]);
  const [linkedInvoices, setLinkedInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Checkout modal state
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({ assigned_to: '', assigned_to_email: '', location: '', notes: '' });
  const [checkinForm, setCheckinForm] = useState({ condition: 'Good', location: '', notes: '' });
  const [processing, setProcessing] = useState(false);
  
  // Deployment checklists state
  const [deploymentChecklists, setDeploymentChecklists] = useState([]);
  
  // User search state
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  // Duplicate modal state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(1);
  const [duplicating, setDuplicating] = useState(false);
  
  // Receipt upload state
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptForm, setReceiptForm] = useState({ description: '', purchase_date: '', amount: '', vendor: '' });
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  
  // Invoice linking state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceResults, setInvoiceResults] = useState([]);
  const [searchingInvoices, setSearchingInvoices] = useState(false);
  const [bcStatus, setBcStatus] = useState(null);
  
  // External purchase state
  const [showExternalPurchaseModal, setShowExternalPurchaseModal] = useState(false);
  const [externalPurchaseForm, setExternalPurchaseForm] = useState({
    order_number: '',
    vendor: 'Lenovo',
    purchase_date: '',
    amount: '',
    payment_method: '',
    description: '',
    order_url: ''
  });

  useEffect(() => {
    fetchData();
    fetchUsers();
    checkBCStatus();
  }, [assetId]);

  const checkBCStatus = async () => {
    try {
      const res = await api.getBCStatus();
      setBcStatus(res.data);
    } catch (err) {
      console.error('BC status check failed:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.getUserDirectory({ limit: 500 });
      // Map the user data to have consistent field names
      const mappedUsers = (res.data.users || []).map(u => ({
        ...u,
        displayName: u.display_name || u.displayName || u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim(),
        mail: u.email || u.mail,
        firstName: u.first_name || u.firstName,
        lastName: u.last_name || u.lastName
      }));
      setUsers(mappedUsers);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch from new database-first API
      const assetRes = await api.getAsset(assetId);
      const assetData = assetRes.data;
      setEndpoint(assetData);
      
      // Set meta from the asset data itself (now stored in DB)
      setMeta({
        department: assetData.department,
        location: assetData.location,
        purchase_date: assetData.purchase_date,
        cost: assetData.purchase_cost,
        warranty_expiration: assetData.warranty_expiration,
        vendor: assetData.vendor,
        po_number: assetData.po_number,
        notes: assetData.notes,
        checkout_status: assetData.checkout_status,
        current_assignee: assetData.assigned_to,
        current_assignee_email: assetData.assigned_to_email,
        checkout_date: assetData.checkout_date,
        checkout_location: assetData.location,
        checkout_notes: assetData.checkout_notes,
        status: assetData.status,
      });
      
      // Also set receipts from asset data
      setReceipts(assetData.receipts || []);
      
      const [historyRes, invoicesRes] = await Promise.all([
        api.getAssetHistory(assetId),
        api.getAssetInvoices(assetData.action1_id || assetId).catch(() => ({ data: { invoices: [] } }))
      ]);
      
      setCheckoutHistory(historyRes.data.checkout_history || []);
      setLinkedInvoices(historyRes.data.linked_invoices || invoicesRes.data.invoices || []);
      setDeploymentChecklists(historyRes.data.deployment_checklists || []);
      
      // Try to get linked tickets if action1_id exists
      if (assetData.action1_id) {
        try {
          const ticketsRes = await api.getAssetTickets(assetData.action1_id);
          setLinkedTickets(ticketsRes.data.tickets || []);
        } catch {
          setLinkedTickets([]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch asset:', err);
      toast.error('Failed to load asset');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateAsset(assetId, {
        department: meta.department,
        location: meta.location,
        purchase_date: meta.purchase_date,
        purchase_cost: meta.cost,
        warranty_expiration: meta.warranty_expiration,
        vendor: meta.vendor,
        po_number: meta.po_number,
        notes: meta.notes,
      });
      toast.success('Asset information saved');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Filter users for search
  const filteredUsers = users.filter(u => {
    if (!userSearch || userSearch.length < 1) return false;
    const search = userSearch.toLowerCase();
    return (u.displayName || '').toLowerCase().includes(search) || 
           (u.mail || '').toLowerCase().includes(search) ||
           (u.firstName || '').toLowerCase().includes(search) ||
           (u.lastName || '').toLowerCase().includes(search);
  }).slice(0, 10);

  // Handle user selection for checkout
  const handleSelectUser = (user) => {
    setCheckoutForm(f => ({
      ...f,
      assigned_to: user.displayName || user.mail,
      assigned_to_email: user.mail
    }));
    setUserSearch('');
    setShowUserDropdown(false);
  };

  // Duplicate asset
  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const res = await api.duplicateAsset(assetId, duplicateCount);
      toast.success(res.data.message);
      setShowDuplicateModal(false);
      // Navigate to assets list to see new assets
      navigate('/assets');
    } catch (err) {
      toast.error('Failed to duplicate asset');
    } finally {
      setDuplicating(false);
    }
  };

  // Upload receipt
  const handleUploadReceipt = async () => {
    if (!receiptFile) {
      toast.error('Please select a file');
      return;
    }
    setUploadingReceipt(true);
    try {
      const formData = new FormData();
      formData.append('file', receiptFile);
      if (receiptForm.description) formData.append('description', receiptForm.description);
      if (receiptForm.purchase_date) formData.append('purchase_date', receiptForm.purchase_date);
      if (receiptForm.amount) formData.append('amount', receiptForm.amount);
      if (receiptForm.vendor) formData.append('vendor', receiptForm.vendor);
      
      await api.uploadReceipt(assetId, formData);
      toast.success('Receipt uploaded');
      setShowReceiptModal(false);
      setReceiptFile(null);
      setReceiptForm({ description: '', purchase_date: '', amount: '', vendor: '' });
      fetchData();
    } catch (err) {
      toast.error('Failed to upload receipt');
    } finally {
      setUploadingReceipt(false);
    }
  };

  // Delete receipt
  const handleDeleteReceipt = async (receiptId) => {
    if (!window.confirm('Delete this receipt?')) return;
    try {
      await api.deleteReceipt(assetId, receiptId);
      toast.success('Receipt deleted');
      setReceipts(receipts.filter(r => r.receipt_id !== receiptId));
    } catch (err) {
      toast.error('Failed to delete receipt');
    }
  };

  const searchInvoices = async (query) => {
    if (!query || query.length < 2) {
      setInvoiceResults([]);
      return;
    }
    setSearchingInvoices(true);
    try {
      const res = await api.getBCInvoices(BC_COMPANY_ID, { search: query, top: 20 });
      setInvoiceResults(res.data.invoices || []);
    } catch (err) {
      console.error('Failed to search invoices:', err);
      toast.error('Failed to search invoices');
    } finally {
      setSearchingInvoices(false);
    }
  };

  const handleLinkInvoice = async (invoice) => {
    setProcessing(true);
    try {
      await api.linkInvoiceToAsset(assetId, {
        invoice_id: invoice.id,
        invoice_number: invoice.number,
        vendor_name: invoice.vendorName,
        invoice_date: invoice.postingDate,
        line_amount: invoice.totalAmountIncludingTax,
        po_number: invoice.vendorInvoiceNumber
      });
      toast.success(`Invoice ${invoice.number} linked to asset`);
      setShowInvoiceModal(false);
      setInvoiceSearch('');
      setInvoiceResults([]);
      fetchData();
    } catch (err) {
      toast.error('Failed to link invoice');
    } finally {
      setProcessing(false);
    }
  };

  const handleAddExternalPurchase = async () => {
    if (!externalPurchaseForm.order_number || !externalPurchaseForm.amount) {
      toast.error('Please enter order number and amount');
      return;
    }
    
    setProcessing(true);
    try {
      await api.addExternalPurchase(assetId, {
        ...externalPurchaseForm,
        amount: parseFloat(externalPurchaseForm.amount)
      });
      toast.success(`Purchase from ${externalPurchaseForm.vendor} recorded`);
      setShowExternalPurchaseModal(false);
      setExternalPurchaseForm({
        order_number: '',
        vendor: 'Lenovo',
        purchase_date: '',
        amount: '',
        payment_method: '',
        description: '',
        order_url: ''
      });
      fetchData();
    } catch (err) {
      toast.error('Failed to record purchase');
    } finally {
      setProcessing(false);
    }
  };

  const handleUnlinkInvoice = async (invoiceId) => {
    if (!window.confirm('Unlink this invoice from the asset?')) return;
    try {
      await api.unlinkInvoiceFromAsset(assetId, invoiceId);
      toast.success('Invoice unlinked');
      fetchData();
    } catch (err) {
      toast.error('Failed to unlink invoice');
    }
  };

  // Debounced invoice search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (showInvoiceModal && invoiceSearch) {
        searchInvoices(invoiceSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [invoiceSearch, showInvoiceModal]);

  const handleCheckout = async () => {
    if (!checkoutForm.assigned_to.trim()) {
      toast.error('Please enter who the asset is being assigned to');
      return;
    }
    
    setProcessing(true);
    try {
      await api.checkoutAsset(assetId, checkoutForm);
      toast.success(`Asset checked out to ${checkoutForm.assigned_to}`);
      setShowCheckoutModal(false);
      setCheckoutForm({ assigned_to: '', assigned_to_email: '', location: '', notes: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to check out asset');
    } finally {
      setProcessing(false);
    }
  };

  const handleCheckin = async () => {
    setProcessing(true);
    try {
      const res = await api.checkinAsset(assetId, checkinForm);
      toast.success(`Asset checked in successfully${res.data.duration_days ? ` (${res.data.duration_days} days)` : ''}`);
      setShowCheckinModal(false);
      setCheckinForm({ condition: 'Good', location: '', notes: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to check in asset');
    } finally {
      setProcessing(false);
    }
  };

  const isOverThreeYears = () => {
    if (!meta.purchase_date) return false;
    const purchaseDate = new Date(meta.purchase_date);
    const threeYearsAgo = new Date();
    threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
    return purchaseDate < threeYearsAgo;
  };

  const getAge = () => {
    if (!meta.purchase_date) return null;
    const purchaseDate = new Date(meta.purchase_date);
    const now = new Date();
    const years = Math.floor((now - purchaseDate) / (365.25 * 24 * 60 * 60 * 1000));
    const months = Math.floor(((now - purchaseDate) % (365.25 * 24 * 60 * 60 * 1000)) / (30.44 * 24 * 60 * 60 * 1000));
    if (years > 0) return `${years}y ${months}m`;
    return `${months}m`;
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString();
  };

  const isCheckedOut = meta.checkout_status === 'checked_out';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Handle invalid asset ID
  if (!assetId || assetId === 'undefined' || assetId === 'null') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <Package className="h-12 w-12 text-slate-300 mb-4" />
        <h1 className="text-xl font-semibold text-slate-700 mb-2">Invalid Asset</h1>
        <p className="text-slate-500 mb-4">The asset ID is missing or invalid.</p>
        <Button onClick={() => navigate('/assets')}>
          Go to Assets
        </Button>
      </div>
    );
  }

  if (!endpoint) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Asset not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/assets')} className="gap-2">
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              {endpoint.effective_status === 'Online' ? (
                <Wifi className="h-5 w-5 text-green-500" />
              ) : (
                <WifiOff className="h-5 w-5 text-gray-400" />
              )}
              <div>
                <div className="flex items-center gap-2">
                  <Input
                    value={endpoint.name || ''}
                    onChange={(e) => setEndpoint(ep => ({ ...ep, name: e.target.value }))}
                    onBlur={async () => {
                      try {
                        await api.updateAsset(assetId, { name: endpoint.name });
                        toast.success('Name updated');
                      } catch (err) {
                        toast.error('Failed to update name');
                      }
                    }}
                    className="font-bold text-slate-800 h-8 w-auto min-w-[200px] border-transparent hover:border-gray-300 focus:border-purple-500"
                  />
                </div>
                <Input
                  value={endpoint.serial || ''}
                  onChange={(e) => setEndpoint(ep => ({ ...ep, serial: e.target.value }))}
                  onBlur={async () => {
                    try {
                      await api.updateAsset(assetId, { serial: endpoint.serial });
                      toast.success('Serial updated');
                    } catch (err) {
                      toast.error('Failed to update serial');
                    }
                  }}
                  className="text-xs text-slate-500 h-6 w-auto min-w-[150px] border-transparent hover:border-gray-300 focus:border-purple-500 font-mono"
                  placeholder="No serial"
                />
              </div>
            </div>
            {meta.status === 'Retired' && (
              <Badge className="bg-gray-200 text-gray-700 cursor-pointer hover:bg-green-100 hover:text-green-700 transition-colors"
                onClick={async () => {
                  try {
                    await api.unretireAsset(assetId);
                    toast.success('Asset restored to active');
                    fetchAsset();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to unretire');
                  }
                }}
                title="Click to restore this asset"
                data-testid="unretire-asset-btn"
              >
                <Archive className="h-3 w-3 mr-1" />
                Retired — Click to Restore
              </Badge>
            )}
            {isCheckedOut && meta.status !== 'Retired' && (
              <Badge className="bg-blue-100 text-blue-700">
                <UserCheck className="h-3 w-3 mr-1" />
                Checked Out
              </Badge>
            )}
            {isOverThreeYears() && (
              <Badge className="bg-orange-100 text-orange-700">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Needs Refresh
              </Badge>
            )}
            {endpoint.reboot_required === 'Yes' && (
              <Badge className="bg-blue-100 text-blue-700">
                <RotateCcw className="h-3 w-3 mr-1" />
                Reboot Required
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowDuplicateModal(true)}
              className="gap-2"
              data-testid="duplicate-asset-btn"
            >
              <Copy className="h-4 w-4" />
              Duplicate
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowReceiptModal(true)}
              className="gap-2"
              data-testid="upload-receipt-btn"
            >
              <Upload className="h-4 w-4" />
              Add Receipt
            </Button>
            <Button 
              variant="outline" 
              onClick={() => window.open(api.getAssetLabelUrl(assetId, 'standard'), '_blank')}
              className="gap-2"
              data-testid="print-label-btn"
            >
              <Printer className="h-4 w-4" />
              Print Label
            </Button>
            <Button 
              variant="outline" 
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/assets/${assetId}/deployment-checklist`, '_blank')}
              className="gap-2"
              data-testid="deployment-checklist-btn"
            >
              <FileText className="h-4 w-4" />
              Deployment Checklist
            </Button>
            <Button 
              variant="outline" 
              onClick={async () => {
                if (!window.confirm('Retire this asset? It will be marked as inactive but not deleted.')) return;
                try {
                  await api.deleteAsset(assetId, false);
                  toast.success('Asset retired');
                  navigate('/assets');
                } catch (err) {
                  toast.error('Failed to retire asset');
                }
              }}
              className="gap-2 text-orange-600 border-orange-200 hover:bg-orange-50"
              data-testid="retire-asset-btn"
            >
              <Archive className="h-4 w-4" />
              Retire
            </Button>
            <Button 
              variant="outline" 
              onClick={async () => {
                if (!window.confirm('PERMANENTLY delete this asset? This cannot be undone.')) return;
                try {
                  await api.deleteAsset(assetId, true);
                  toast.success('Asset permanently deleted');
                  navigate('/assets');
                } catch (err) {
                  toast.error('Failed to delete asset');
                }
              }}
              className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
              data-testid="delete-asset-btn"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700 gap-2">
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </header>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Check-In / Check-Out Card */}
            <Card className={isCheckedOut ? "border-blue-200 bg-blue-50" : "border-green-200 bg-green-50"}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isCheckedOut ? (
                      <UserCheck className="h-4 w-4 text-blue-600" />
                    ) : (
                      <UserMinus className="h-4 w-4 text-green-600" />
                    )}
                    <span className={isCheckedOut ? "text-blue-700" : "text-green-700"}>
                      {isCheckedOut ? 'Currently Checked Out' : 'Available'}
                    </span>
                  </div>
                  {isCheckedOut ? (
                    <Button 
                      size="sm" 
                      onClick={() => setShowCheckinModal(true)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <UserMinus className="h-4 w-4 mr-1" />
                      Check In
                    </Button>
                  ) : (
                    <Button 
                      size="sm" 
                      onClick={() => setShowCheckoutModal(true)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <UserCheck className="h-4 w-4 mr-1" />
                      Check Out
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isCheckedOut ? (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Assigned To</span>
                      <p className="font-medium">{meta.current_assignee || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Checkout Date</span>
                      <p className="font-medium">{formatDate(meta.checkout_date)}</p>
                    </div>
                    {meta.current_assignee_email && (
                      <div>
                        <span className="text-gray-500">Email</span>
                        <p className="font-medium">{meta.current_assignee_email}</p>
                      </div>
                    )}
                    {meta.checkout_location && (
                      <div>
                        <span className="text-gray-500">Location</span>
                        <p className="font-medium">{meta.checkout_location}</p>
                      </div>
                    )}
                    {meta.checkout_notes && (
                      <div className="col-span-2">
                        <span className="text-gray-500">Notes</span>
                        <p className="font-medium">{meta.checkout_notes}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-green-600">This asset is available for checkout.</p>
                )}
              </CardContent>
            </Card>

            {/* System Status Alert */}
            {(endpoint.reboot_required === 'Yes' || (endpoint.missing_updates?.critical || 0) > 0 || (endpoint.vulnerabilities?.critical || 0) > 0) && (
              <Card className="border-orange-200 bg-orange-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-orange-700">
                    <AlertTriangle className="h-4 w-4" />
                    Action Required
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4">
                    {endpoint.reboot_required === 'Yes' && (
                      <div className="flex items-center gap-2 text-blue-700 bg-blue-100 px-3 py-2 rounded-lg">
                        <RotateCcw className="h-4 w-4" />
                        <span className="text-sm font-medium">Reboot Required</span>
                      </div>
                    )}
                    {(endpoint.missing_updates?.critical || 0) > 0 && (
                      <div className="flex items-center gap-2 text-orange-700 bg-orange-100 px-3 py-2 rounded-lg">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="text-sm font-medium">{endpoint.missing_updates.critical} Critical Updates</span>
                      </div>
                    )}
                    {(endpoint.vulnerabilities?.critical || 0) > 0 && (
                      <div className="flex items-center gap-2 text-red-700 bg-red-100 px-3 py-2 rounded-lg">
                        <AlertCircle className="h-4 w-4" />
                        <span className="text-sm font-medium">{endpoint.vulnerabilities.critical} Vulnerabilities</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Hardware Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Cpu className="h-4 w-4" />
                  Hardware Specifications
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Asset Type</span>
                    <Select 
                      value={endpoint.asset_type || 'Computer'} 
                      onValueChange={async (v) => {
                        let newType = v;
                        if (v === '__custom__') {
                          newType = window.prompt('Enter custom asset type:');
                          if (!newType) return;
                        }
                        try {
                          await api.updateAsset(assetId, { asset_type: newType });
                          setEndpoint(e => ({ ...e, asset_type: newType }));
                          toast.success('Asset type updated');
                        } catch (err) {
                          toast.error('Failed to update');
                        }
                      }}
                    >
                      <SelectTrigger className="h-8 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Computer">Computer</SelectItem>
                        <SelectItem value="Laptop">Laptop</SelectItem>
                        <SelectItem value="Desktop">Desktop</SelectItem>
                        <SelectItem value="Server">Server</SelectItem>
                        <SelectItem value="Printer">Printer</SelectItem>
                        <SelectItem value="Monitor">Monitor</SelectItem>
                        <SelectItem value="Network">Network Device</SelectItem>
                        <SelectItem value="Phone">Phone</SelectItem>
                        <SelectItem value="Tablet">Tablet</SelectItem>
                        <SelectItem value="Peripheral">Peripheral</SelectItem>
                        <SelectItem value="Software">Software License</SelectItem>
                        <SelectItem value="Furniture">Furniture</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                        <SelectItem value="__custom__">+ Custom Type...</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <span className="text-gray-500">Manufacturer</span>
                    <Input
                      value={endpoint.manufacturer || ''}
                      onChange={(e) => setEndpoint(ep => ({ ...ep, manufacturer: e.target.value }))}
                      onBlur={async () => {
                        try {
                          await api.updateAsset(assetId, { manufacturer: endpoint.manufacturer });
                          toast.success('Manufacturer updated');
                        } catch (err) {
                          toast.error('Failed to update');
                        }
                      }}
                      className="font-medium h-8 border-transparent hover:border-gray-300 focus:border-purple-500"
                      placeholder="-"
                    />
                  </div>
                  <div>
                    <span className="text-gray-500">Model</span>
                    <Input
                      value={endpoint.model || ''}
                      onChange={(e) => setEndpoint(ep => ({ ...ep, model: e.target.value }))}
                      onBlur={async () => {
                        try {
                          await api.updateAsset(assetId, { model: endpoint.model });
                          toast.success('Model updated');
                        } catch (err) {
                          toast.error('Failed to update');
                        }
                      }}
                      className="font-medium h-8 border-transparent hover:border-gray-300 focus:border-purple-500"
                      placeholder="-"
                    />
                  </div>
                  <div>
                    <span className="text-gray-500">Operating System</span>
                    <p className="font-medium">{endpoint.OS || endpoint.os || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">CPU</span>
                    <p className="font-medium">{endpoint.CPU_name || endpoint.cpu || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">RAM</span>
                    <p className="font-medium">{endpoint.RAM || endpoint.ram || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Storage</span>
                    <p className="font-medium">{endpoint.disk || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Serial Number</span>
                    <Input
                      value={endpoint.serial || ''}
                      onChange={(e) => setEndpoint(ep => ({ ...ep, serial: e.target.value }))}
                      onBlur={async () => {
                        try {
                          await api.updateAsset(assetId, { serial: endpoint.serial });
                          toast.success('Serial number updated');
                        } catch (err) {
                          toast.error('Failed to update');
                        }
                      }}
                      className="font-medium font-mono h-8 border-transparent hover:border-gray-300 focus:border-purple-500"
                      placeholder="-"
                    />
                  </div>
                  <div>
                    <span className="text-gray-500">IP Address</span>
                    <p className="font-medium font-mono">{endpoint.address || endpoint.ip_address || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">MAC Address</span>
                    <p className="font-medium font-mono">{endpoint.MAC || endpoint.mac || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Checkout History */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Checkout History ({checkoutHistory.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {checkoutHistory.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No checkout history</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {checkoutHistory.slice().reverse().map((record, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg border ${
                          record.action === 'checkout' 
                            ? 'bg-blue-50 border-blue-200' 
                            : 'bg-green-50 border-green-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <Badge className={record.action === 'checkout' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                            {record.action === 'checkout' ? 'Checked Out' : 'Checked In'}
                          </Badge>
                          <span className="text-xs text-gray-500">{formatDate(record.timestamp)}</span>
                        </div>
                        <div className="text-sm">
                          {record.action === 'checkout' ? (
                            <span>Assigned to <strong>{record.assigned_to}</strong></span>
                          ) : (
                            <span>Returned by <strong>{record.returned_by}</strong>
                              {record.checkout_duration_days !== null && (
                                <span className="text-gray-500"> ({record.checkout_duration_days} days)</span>
                              )}
                            </span>
                          )}
                        </div>
                        {record.location && <p className="text-xs text-gray-500 mt-1">Location: {record.location}</p>}
                        {record.condition && <p className="text-xs text-gray-500">Condition: {record.condition}</p>}
                        {record.notes && <p className="text-xs text-gray-500 mt-1">Notes: {record.notes}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Deployment Checklists */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Deployment Checklists ({deploymentChecklists.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deploymentChecklists.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {deploymentChecklists.slice().reverse().map((checklist, idx) => (
                      <div 
                        key={checklist.checklist_id || idx} 
                        className="p-3 rounded-lg border bg-purple-50 border-purple-200 hover:bg-purple-100 transition-colors group"
                        data-testid={`checklist-${checklist.checklist_id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <Badge className={checklist.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {checklist.status === 'completed' ? 'Completed' : 'In Progress'}
                          </Badge>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{formatDate(checklist.created_at)}</span>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (window.confirm(`Delete checklist for ${checklist.employee_name}?`)) {
                                  try {
                                    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assets/${assetId}/deployment-checklist/${checklist.checklist_id}`, {
                                      method: 'DELETE'
                                    });
                                    setDeploymentChecklists(prev => prev.filter(c => c.checklist_id !== checklist.checklist_id));
                                  } catch (err) {
                                    alert('Failed to delete: ' + err.message);
                                  }
                                }
                              }}
                              className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 p-1 rounded transition-opacity"
                              title="Delete checklist"
                              data-testid={`delete-checklist-${checklist.checklist_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <div 
                          className="text-sm cursor-pointer"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/assets/${assetId}/deployment-checklist/${checklist.checklist_id}/view`, '_blank')}
                        >
                          <span>Deployed to <strong>{checklist.employee_name}</strong></span>
                        </div>
                        {checklist.completed_items !== undefined && (
                          <div className="text-xs text-gray-500 mt-1">
                            Progress: {checklist.completed_items}/{checklist.total_items} items completed
                          </div>
                        )}
                        {checklist.location_department && (
                          <p className="text-xs text-gray-500">Dept: {checklist.location_department}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No deployment checklists yet. Click "Deployment Checklist" button above to create one.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Security Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Security Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Missing Updates</div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-orange-600">
                        {endpoint.missing_updates?.critical || 0}
                      </span>
                      <span className="text-sm text-gray-500">critical</span>
                      <span className="text-gray-300">|</span>
                      <span className="text-sm text-gray-500">
                        {endpoint.missing_updates?.other || 0} other
                      </span>
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Vulnerabilities</div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-red-600">
                        {endpoint.vulnerabilities?.critical || 0}
                      </span>
                      <span className="text-sm text-gray-500">critical</span>
                      <span className="text-gray-300">|</span>
                      <span className="text-sm text-gray-500">
                        {endpoint.vulnerabilities?.other || 0} other
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Linked Tickets */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Ticket className="h-4 w-4" />
                  Related Tickets ({linkedTickets.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {linkedTickets.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No tickets linked to this device</p>
                ) : (
                  <div className="space-y-2">
                    {linkedTickets.slice(0, 10).map(ticket => (
                      <div 
                        key={ticket.ticket_id} 
                        className="p-2 bg-gray-50 rounded flex items-center justify-between cursor-pointer hover:bg-gray-100"
                        onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                      >
                        <div>
                          <span className="text-sm text-gray-500">#{ticket.ticket_number}</span>
                          <span className="ml-2 font-medium text-sm">{ticket.summary}</span>
                        </div>
                        <Badge className={`text-xs ${ticket.status === 'Closed' ? 'bg-gray-100' : 'bg-blue-100'}`}>
                          {ticket.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Uploaded Receipts/Attachments */}
            <Card className="border-purple-200">
              <CardHeader>
                <CardTitle className="text-sm flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-purple-600" />
                    Receipts & Attachments ({receipts.length})
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setShowReceiptModal(true)}
                    className="gap-1 border-purple-300 text-purple-600 hover:bg-purple-50"
                  >
                    <Upload className="h-3 w-3" />
                    Upload
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {receipts.length === 0 ? (
                  <div className="text-center py-4">
                    <FileText className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm text-gray-400">No receipts uploaded</p>
                    <p className="text-xs text-gray-400 mt-1">Upload purchase receipts, invoices, or warranty docs</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {receipts.map((r) => (
                      <div 
                        key={r.receipt_id}
                        className="p-3 bg-purple-50 rounded-lg border border-purple-200 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-purple-100 rounded">
                            {r.content_type?.includes('pdf') ? (
                              <FileText className="h-4 w-4 text-purple-600" />
                            ) : (
                              <Receipt className="h-4 w-4 text-purple-600" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium">{r.original_name}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              {r.vendor && <span>{r.vendor}</span>}
                              {r.amount && <span>• ${parseFloat(r.amount).toLocaleString()}</span>}
                              {r.purchase_date && <span>• {r.purchase_date}</span>}
                            </div>
                            {r.description && <p className="text-xs text-gray-400">{r.description}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(api.getReceiptDownloadUrl(assetId, r.receipt_id), '_blank')}
                            className="h-8 w-8 p-0"
                          >
                            <Download className="h-4 w-4 text-gray-500" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteReceipt(r.receipt_id)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Linked Invoices from Business Central */}
            <Card className="border-green-200">
              <CardHeader>
                <CardTitle className="text-sm flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-green-600" />
                    Linked Invoices ({linkedInvoices.length})
                  </div>
                  <div className="flex gap-2">
                    {bcStatus?.connected && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setShowInvoiceModal(true)}
                        className="gap-1"
                      >
                        <Link2 className="h-3 w-3" />
                        BC Invoice
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setShowExternalPurchaseModal(true)}
                      className="gap-1 border-blue-300 text-blue-600 hover:bg-blue-50"
                    >
                      <FileText className="h-3 w-3" />
                      External Order
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {linkedInvoices.length === 0 && !meta.purchase_history?.length ? (
                  <div className="text-center py-4">
                    <Receipt className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm text-gray-400">No purchases linked</p>
                    <p className="text-xs text-gray-400 mt-1">Link BC invoices or add external orders (Lenovo.com, etc.)</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* External purchases */}
                    {meta.purchase_history?.map((p, idx) => (
                      <div 
                        key={`ext-${idx}`}
                        className="p-3 bg-blue-50 rounded-lg border border-blue-200"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <Badge className="text-xs bg-blue-100 text-blue-700">External</Badge>
                              <span className="font-medium text-sm">{p.vendor}</span>
                            </div>
                            <p className="text-xs text-gray-600 mt-1">Order #{p.order_number}</p>
                            <p className="text-xs text-gray-500">{p.purchase_date}</p>
                            {p.amount && (
                              <p className="text-sm font-medium text-blue-700 mt-1">
                                ${p.amount.toLocaleString()}
                              </p>
                            )}
                            {p.payment_method && (
                              <p className="text-xs text-gray-400">{p.payment_method}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* BC invoices */}
                    {linkedInvoices.map((inv, idx) => (
                      <div 
                        key={`bc-${idx}`}
                        className="p-3 bg-green-50 rounded-lg border border-green-200"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <Badge className="text-xs bg-green-100 text-green-700">BC Invoice</Badge>
                              <span className="font-medium text-sm">#{inv.invoice_number}</span>
                            </div>
                            <p className="text-xs text-gray-600 mt-1">{inv.vendor_name}</p>
                            <p className="text-xs text-gray-500">{inv.invoice_date}</p>
                            {inv.line_amount && (
                              <p className="text-sm font-medium text-green-700 mt-1">
                                ${inv.line_amount.toLocaleString()}
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 h-6 px-2"
                            onClick={() => handleUnlinkInvoice(inv.invoice_id)}
                          >
                            <Unlink className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Assignment */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Assignment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Current User (from Action1)</label>
                  <p className="font-medium">
                    {endpoint.action1_current_user || 
                     endpoint.assigned_to || 
                     (endpoint.action1_user ? endpoint.action1_user.split('\\').pop() : null) || 
                     (endpoint.user ? endpoint.user.split('\\').pop() : null) || 
                     'None'}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Department</label>
                  <Select 
                    value={meta.department || 'none'} 
                    onValueChange={(v) => setMeta(m => ({ ...m, department: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Select --</SelectItem>
                      {DEPARTMENTS.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Location</label>
                  <Input 
                    value={meta.location || ''} 
                    onChange={(e) => setMeta(m => ({ ...m, location: e.target.value }))}
                    placeholder="e.g., Building A, Room 101"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Purchase Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Purchase Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Purchase Date</label>
                  <Input 
                    type="date"
                    value={meta.purchase_date || ''} 
                    onChange={(e) => setMeta(m => ({ ...m, purchase_date: e.target.value }))}
                  />
                  {meta.purchase_date && (
                    <p className="text-xs text-gray-400 mt-1">
                      Age: {getAge()} {isOverThreeYears() && <span className="text-orange-500">(refresh recommended)</span>}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Purchase Cost ($)</label>
                  <Input 
                    type="number"
                    value={meta.cost || ''} 
                    onChange={(e) => setMeta(m => ({ ...m, cost: parseFloat(e.target.value) || '' }))}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Warranty Expiration</label>
                  <Input 
                    type="date"
                    value={meta.warranty_expiration || ''} 
                    onChange={(e) => setMeta(m => ({ ...m, warranty_expiration: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Vendor/Supplier</label>
                  <Input 
                    value={meta.vendor || ''} 
                    onChange={(e) => setMeta(m => ({ ...m, vendor: e.target.value }))}
                    placeholder="e.g., Dell, CDW"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">PO Number</label>
                  <Input 
                    value={meta.po_number || ''} 
                    onChange={(e) => setMeta(m => ({ ...m, po_number: e.target.value }))}
                    placeholder="PO-12345"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <textarea
                  className="w-full border rounded p-2 text-sm min-h-[100px]"
                  value={meta.notes || ''}
                  onChange={(e) => setMeta(m => ({ ...m, notes: e.target.value }))}
                  placeholder="Add notes about this device..."
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Checkout Modal */}
      <Dialog open={showCheckoutModal} onOpenChange={setShowCheckoutModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Check Out Asset</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="relative">
              <label className="text-sm font-medium mb-1 block">Assign To *</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={userSearch || checkoutForm.assigned_to}
                  onChange={(e) => {
                    setUserSearch(e.target.value);
                    setCheckoutForm(f => ({ ...f, assigned_to: e.target.value }));
                    setShowUserDropdown(true);
                  }}
                  onFocus={() => setShowUserDropdown(true)}
                  placeholder="Search users..."
                  className="pl-10"
                />
              </div>
              {showUserDropdown && filteredUsers.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                  {filteredUsers.map((user) => (
                    <div
                      key={user.id || user.mail}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleSelectUser(user)}
                    >
                      <div className="font-medium text-sm">{user.displayName}</div>
                      <div className="text-xs text-gray-500">{user.mail}</div>
                    </div>
                  ))}
                </div>
              )}
              {showUserDropdown && userSearch && userSearch.length > 0 && filteredUsers.length === 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-3 text-sm text-gray-500">
                  No users found
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Email</label>
              <Input
                type="email"
                value={checkoutForm.assigned_to_email}
                onChange={(e) => setCheckoutForm(f => ({ ...f, assigned_to_email: e.target.value }))}
                placeholder="user@company.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Location</label>
              <Input
                value={checkoutForm.location}
                onChange={(e) => setCheckoutForm(f => ({ ...f, location: e.target.value }))}
                placeholder="e.g., Remote, Building A"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <textarea
                className="w-full border rounded p-2 text-sm min-h-[80px]"
                value={checkoutForm.notes}
                onChange={(e) => setCheckoutForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Any additional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckoutModal(false)}>Cancel</Button>
            <Button onClick={handleCheckout} disabled={processing} className="bg-blue-600 hover:bg-blue-700">
              {processing ? 'Processing...' : 'Check Out'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Checkin Modal */}
      <Dialog open={showCheckinModal} onOpenChange={setShowCheckinModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Check In Asset</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded">
              <p className="text-sm text-gray-600">
                Returning from: <strong>{meta.current_assignee}</strong>
              </p>
              {meta.checkout_date && (
                <p className="text-xs text-gray-500 mt-1">
                  Checked out: {formatDate(meta.checkout_date)}
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Condition</label>
              <Select 
                value={checkinForm.condition} 
                onValueChange={(v) => setCheckinForm(f => ({ ...f, condition: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Excellent">Excellent</SelectItem>
                  <SelectItem value="Good">Good</SelectItem>
                  <SelectItem value="Fair">Fair</SelectItem>
                  <SelectItem value="Poor">Poor</SelectItem>
                  <SelectItem value="Damaged">Damaged</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Return Location</label>
              <Input
                value={checkinForm.location}
                onChange={(e) => setCheckinForm(f => ({ ...f, location: e.target.value }))}
                placeholder="e.g., IT Storage, Building A"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <textarea
                className="w-full border rounded p-2 text-sm min-h-[80px]"
                value={checkinForm.notes}
                onChange={(e) => setCheckinForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Any issues or notes about the return..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckinModal(false)}>Cancel</Button>
            <Button onClick={handleCheckin} disabled={processing} className="bg-green-600 hover:bg-green-700">
              {processing ? 'Processing...' : 'Check In'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Search Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-green-600" />
              Link Invoice from Business Central
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                className="pl-10"
                placeholder="Search by invoice number or vendor..."
                value={invoiceSearch}
                onChange={(e) => setInvoiceSearch(e.target.value)}
                autoFocus
              />
            </div>
            
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <FileText className="h-3 w-3" />
              <span>READ-ONLY: Data is fetched from Business Central</span>
            </div>
            
            <div className="max-h-72 overflow-y-auto space-y-2">
              {searchingInvoices ? (
                <div className="text-center py-4">
                  <div className="animate-spin h-5 w-5 border-2 border-green-500 border-t-transparent rounded-full mx-auto"></div>
                </div>
              ) : invoiceResults.length > 0 ? (
                invoiceResults.map((inv) => (
                  <div
                    key={inv.id}
                    className="p-3 border rounded-lg hover:bg-green-50 hover:border-green-300 cursor-pointer transition-colors"
                    onClick={() => handleLinkInvoice(inv)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Invoice #{inv.number}</p>
                        <p className="text-sm text-gray-600">{inv.vendorName}</p>
                        <p className="text-xs text-gray-400">{inv.postingDate}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-green-700">
                          ${(inv.totalAmountIncludingTax || 0).toLocaleString()}
                        </p>
                        {inv.vendorInvoiceNumber && (
                          <p className="text-xs text-gray-400">PO: {inv.vendorInvoiceNumber}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : invoiceSearch.length >= 2 ? (
                <p className="text-center text-gray-400 py-4">No invoices found matching "{invoiceSearch}"</p>
              ) : (
                <p className="text-center text-gray-400 py-4">Type at least 2 characters to search</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceModal(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* External Purchase Modal */}
      <Dialog open={showExternalPurchaseModal} onOpenChange={setShowExternalPurchaseModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Add External Purchase
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-xs text-gray-500">
              Record a purchase from Lenovo.com, Amazon, or other external sources.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Vendor *</label>
                <Input
                  value={externalPurchaseForm.vendor}
                  onChange={(e) => setExternalPurchaseForm(f => ({ ...f, vendor: e.target.value }))}
                  placeholder="Lenovo"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Order Number *</label>
                <Input
                  value={externalPurchaseForm.order_number}
                  onChange={(e) => setExternalPurchaseForm(f => ({ ...f, order_number: e.target.value }))}
                  placeholder="4648386702"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Purchase Date</label>
                <Input
                  type="date"
                  value={externalPurchaseForm.purchase_date}
                  onChange={(e) => setExternalPurchaseForm(f => ({ ...f, purchase_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Amount *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={externalPurchaseForm.amount}
                  onChange={(e) => setExternalPurchaseForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="3893.84"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Payment Method</label>
              <Input
                value={externalPurchaseForm.payment_method}
                onChange={(e) => setExternalPurchaseForm(f => ({ ...f, payment_method: e.target.value }))}
                placeholder="VISA •••• 7693 (Ramp)"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Input
                value={externalPurchaseForm.description}
                onChange={(e) => setExternalPurchaseForm(f => ({ ...f, description: e.target.value }))}
                placeholder="ThinkPad P14s Gen 6"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExternalPurchaseModal(false)}>Cancel</Button>
            <Button 
              onClick={handleAddExternalPurchase} 
              disabled={processing}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {processing ? 'Saving...' : 'Add Purchase'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Asset Modal */}
      <Dialog open={showDuplicateModal} onOpenChange={setShowDuplicateModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Duplicate Asset</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              Create copies of <strong>{endpoint?.name}</strong> for bulk orders.
              Each copy will have a unique name (e.g., {endpoint?.name?.replace(/-?\d+$/, '')}-001, -002, etc.)
            </p>
            <Label>Number of copies</Label>
            <Input
              type="number"
              min="1"
              max="50"
              value={duplicateCount}
              onChange={(e) => setDuplicateCount(parseInt(e.target.value) || 1)}
              className="mt-1"
            />
            <p className="text-xs text-gray-400 mt-1">Max 50 at a time</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateModal(false)}>Cancel</Button>
            <Button onClick={handleDuplicate} disabled={duplicating} className="bg-purple-600 hover:bg-purple-700">
              {duplicating ? 'Creating...' : `Create ${duplicateCount} Copy(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Receipt / Invoice</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>File *</Label>
              <Input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                className="mt-1"
              />
              <p className="text-xs text-gray-400 mt-1">JPEG, PNG, PDF (max 10MB)</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vendor</Label>
                <Input
                  placeholder="e.g., Amazon, Lenovo"
                  value={receiptForm.vendor}
                  onChange={(e) => setReceiptForm(f => ({ ...f, vendor: e.target.value }))}
                />
              </div>
              <div>
                <Label>Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={receiptForm.amount}
                  onChange={(e) => setReceiptForm(f => ({ ...f, amount: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Purchase Date</Label>
              <Input
                type="date"
                value={receiptForm.purchase_date}
                onChange={(e) => setReceiptForm(f => ({ ...f, purchase_date: e.target.value }))}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                placeholder="Optional notes about this receipt"
                value={receiptForm.description}
                onChange={(e) => setReceiptForm(f => ({ ...f, description: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceiptModal(false)}>Cancel</Button>
            <Button onClick={handleUploadReceipt} disabled={uploadingReceipt || !receiptFile} className="bg-purple-600 hover:bg-purple-700">
              {uploadingReceipt ? 'Uploading...' : 'Upload Receipt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetDetailPage;
